import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { airportTaxiActionCreators } from "../airportTaxiModule";
// helpers
import { getSummaryRouteTitle } from "../helpers/getSummaryRouteTitle";
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";

interface AirportTaxiSummaryTypes {
  onBookARideBtn: any;
  selectedFromStation: any;
  selectedToStation: any;
  cancellationPolicy?: string | null;
}

export default function AirportTaxiSummary({
  onBookARideBtn,
  selectedFromStation,
  selectedToStation,
  cancellationPolicy
}: AirportTaxiSummaryTypes) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";
  const [checkPhoneValidation, setCheckPhoneValidation] = useState(false);

  // states
  const platformType = useSelector((state: any) => state.global.platformType);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const dataForBooking = useSelector((state: any) => state.airportTaxi.dataForBooking);
  const airportTaxiInitialItem = useSelector((state: any) => state.airportTaxi.airportTaxiInitialItem);

  const airportTaxiEstimation = useSelector((state: any) => state.airportTaxi.airportTaxiEstimation);
  const errorField = useSelector((state: any) => state.errorField.errorField);

  const airportTaxiBookingError = useSelector((state: any) => state.airportTaxi.bookingError);
  const airportTaxiBookingErrorMessage = useSelector((state: any) => state.airportTaxi.bookingErrorMessage);

  // actions
  const updateFormForBooking = useActions(airportTaxiActionCreators?.updateFormForBookingAction, []);
  const toggleEstimationAvailableState = useActions(
    airportTaxiActionCreators?.toggleEstimationAvailableStateAction,
    []
  );
  const toggleAirportTaxiBookingError = useActions(airportTaxiActionCreators?.toggleBookingErrorAction, []);

  const isAirline = platformType === "airline";

  useEffect(() => {
    if (airportTaxiBookingErrorMessage) {
      const hasPhoneField = airportTaxiBookingErrorMessage.some((f: any) => f.field === "phone");
      setCheckPhoneValidation(hasPhoneField);
    }
  }, [airportTaxiBookingErrorMessage]);

  // Handle Forms //

  const handleBookingFormInputChange = (e: any, input: any) => {
    switch (input) {
      case "nameForm":
        updateFormForBooking({
          ...dataForBooking,
          nameForm: e.target.value
        });
        break;
      case "emailForm":
        updateFormForBooking({
          ...dataForBooking,
          emailForm: e.target.value
        });
        break;
      case "mobileNumberForm":
        updateFormForBooking({
          ...dataForBooking,
          mobileNumberForm: e.target.value
        });
        break;
      case "commentForm":
        updateFormForBooking({
          ...dataForBooking,
          commentForm: e.target.value
        });
        break;
      case "acceptTerms":
        updateFormForBooking({
          ...dataForBooking,
          acceptTerms: e.target.checked
        });
        break;
      default:
        break;
    }
  };

  // END OF Handle Forms //

  const renderBookErrorMsg = () => {
    // fallback error message, if there are no error messages from API
    if (!airportTaxiBookingErrorMessage || airportTaxiBookingErrorMessage?.length === 0) {
      return (
        <Box mt={1} className="airport-taxi__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      );
    }

    return airportTaxiBookingErrorMessage?.map((error: any) => (
      <Box mt={1} className="airport-taxi__error-msg" key={error?.field || Math.random()}>
        {error?.errorMessage ? `${error.errorMessage}` : t("transport.prebookCustomError")}
      </Box>
    ));
  };

  const handeEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      toggleEstimationAvailableState();
      toggleAirportTaxiBookingError(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
          <Box>
            <Box className="summary-option-title" mb={2}>
              {t("transport.ticketOption")}
            </Box>
            <Box className="summary-option-title default-text-transform">
              {getSummaryRouteTitle(
                t,
                selectedFromStation?.value?.structured_formatting?.main_text,
                selectedToStation?.value?.structured_formatting?.main_text
              )}
            </Box>
          </Box>
          <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
            <AppButton
              label={t("transport.editBtn")}
              onClick={handeEditTicketOptionButton}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
            />
          </Grid>
        </Box>
        {/* Grey box is removed for Airport Taxi in US: User Story 5637: Transferhub: (FE) Remove grey ticket-detail box from Airport Taxi booking page, for both TH and GP */}
        {/* <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2} pl={{ xs: 0, sm: 2 }}>
          <Box
            sx={{
              display: "flex",
              textAlign: { xs: "center", sm: "left" },
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" }
            }}
          >
            <Grid item xs={3} pl={{ xs: 1, sm: 0 }} className="summary-passenger-box__title">
              {t("transport.invoiceRides")}
            </Grid>
            <Grid item xs={3} className="summary-passenger-box__title">
              {t("transport.price")}
            </Grid>

            <Grid item xs={3} className="summary-passenger-box__title">
              {t("transport.arrivalIn")}
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              textAlign: { xs: "center", sm: "left" },
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" }
            }}
          >
            <Grid item xs={3} pl={{ xs: 1, sm: 0 }}>
              {t("transport.creditCard")}
            </Grid>
            <Grid item xs={3}>
              {airportTaxiEstimation?.formatted_price || ""}
            </Grid>

            <Grid item xs={3}>
              {airportTaxiEstimation?.details?.arrival_estimation_description || ""}
            </Grid>
          </Box>
        </Grid> */}
        <Box mt={4} mb={2}>
          <Box sx={{ display: "flex", alignItems: "center" }} />
          <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
            {t("transport.totalPrice")}: {airportTaxiEstimation?.formatted_price || ""}
          </Box>
        </Box>
      </Box>

      <Grid container mt={2}>
        {/* Left side: booking form */}
        <Grid item xs={12} md={6}>
          <Grid container width={{ sm: "100%", md: "100%" }}>
            {airportTaxiInitialItem?.booking_setting?.customer_name !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestNameLabel")}{" "}
                    {airportTaxiInitialItem?.booking_setting?.customer_name === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"nameForm"}
                    type="string"
                    value={dataForBooking?.nameForm}
                    onChange={event => handleBookingFormInputChange(event, "nameForm")}
                    error={errorField?.nameForm}
                  />
                </Grid>
              </Grid>
            )}
            {airportTaxiInitialItem?.booking_setting?.customer_email !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestEmailLabel")}{" "}
                    {airportTaxiInitialItem?.booking_setting?.customer_email === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"emailForm"}
                    type="string"
                    value={dataForBooking?.emailForm}
                    onChange={event => handleBookingFormInputChange(event, "emailForm")}
                    error={errorField?.emailForm}
                  />
                </Grid>
              </Grid>
            )}
            {airportTaxiInitialItem?.booking_setting?.customer_phone !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestPhoneLabel")}{" "}
                    {airportTaxiInitialItem?.booking_setting?.customer_phone === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"mobileNumberForm"}
                    type="string"
                    value={dataForBooking?.mobileNumberForm}
                    onChange={event => handleBookingFormInputChange(event, "mobileNumberForm")}
                    error={errorField?.mobileNumberForm || checkPhoneValidation}
                  />
                  <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("phoneNumberLabel")}</Box>
                </Grid>
              </Grid>
            )}
            {airportTaxiInitialItem?.booking_setting?.comment !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.commentLabel")}{" "}
                    {airportTaxiInitialItem?.booking_setting?.comment === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"commentForm"}
                    type="string"
                    value={dataForBooking?.commentForm}
                    onChange={event => handleBookingFormInputChange(event, "commentForm")}
                    error={errorField?.commentForm}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Right side: Miles & More info (responsive: below on mobile) */}
        {shouldShowMilesAndMore(airportTaxiEstimation?.miles_and_more, isAirline) && (
          <Grid
            item
            xs={12}
            md={6}
            pl={{ xs: 0, md: 5 }}
            pt={{ xs: 4, md: 0 }}
            pb={{ xs: 0, md: 6 }}
            sx={{ alignItems: "center", justifyContent: "center", display: "flex" }}
          >
            <MilesAndMoreInfo miles={airportTaxiEstimation?.miles_and_more} />
          </Grid>
        )}
      </Grid>

      {airportTaxiBookingError && renderBookErrorMsg()}

      {cancellationPolicy && (
        <Box pt={6}>
          <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
        </Box>
      )}

      <Grid
        item
        xs={12}
        sx={{
          width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
          margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "25px auto 0 auto",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 2 }
        }}
      >
        {isAirline && (
          <AppButton
            label={t("transport.goBack")}
            onClick={() => navigateBackOrToMain(navigate, { language })}
            disableRipple={true}
            customBackgroundColor={theme.palette.primary.dark}
            customHoverColor={theme.palette.primary.dark}
            fontSize="14px"
          />
        )}
        <AppButton
          label={t("transport.bookButton")}
          onClick={onBookARideBtn}
          disableRipple={true}
          customBackgroundColor={tenantInfo?.style?.button?.color}
          customActiveColor={tenantInfo?.style?.button?.active}
          customHoverColor={tenantInfo?.style?.button?.hover}
          customClickColor={tenantInfo?.style?.button?.click}
          fontSize="14px"
          disabled={false}
        />
      </Grid>
    </Box>
  );
}
